<template>
  <div class="media-upload">
    <div class="upload-wrapper">
      <el-upload
        ref="uploadRef"
        :action="uploadUrl"
        :headers="uploadHeaders"
        :accept="acceptTypes"
        :before-upload="handleBeforeUpload"
        :on-success="handleSuccess"
        :on-error="handleError"
        :on-remove="handleRemove"
        :multiple="false"
        :limit="1"
        :show-file-list="false"
        list-type="picture-card">
        <i v-if="!displayUrl" class="el-icon-plus"></i>
        <div v-else class="uploaded-image">
          <img v-if="displayMode === 'image'" :src="displayUrl" alt="" />
          <div v-else class="uploaded-video-placeholder">
            <i class="el-icon-video-camera-solid"></i>
            <span>{{ $t("mediaUpload.videoReady") || "Video uploaded" }}</span>
          </div>
        </div>
      </el-upload>
      <div v-if="uploading" class="upload-loading">
        <i class="el-icon-loading"></i>
      </div>
    </div>
    <div v-if="tips && tips.length > 0" class="upload-tips">
      <div v-for="(tip, index) in tips" :key="index" class="tip-item">
        {{ tip }}
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import config from "@/http/config";

export default {
  name: "MediaUpload",
  props: {
    action: {
      type: String,
      default: ""
    },
    accept: {
      type: String,
      default: ".jpg,.jpeg,.png,.gif,.mp4,.avi,.mov,.wmv,.webm"
    },
    imageMaxSize: {
      type: Number,
      default: 5
    },
    videoMaxSize: {
      type: Number,
      default: 100
    },
    tips: {
      type: Array,
      default: () => []
    },
    value: {
      type: String,
      default: ""
    },
    valueType: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      fileUrl: "",
      fileType: "", // 'image' 或 'video'
      posterUrl: "", // 视频首帧预览
      duration: 0,
      uploading: false,
      generatingPoster: false
    };
  },
  computed: {
    ...mapGetters(["token"]),
    uploadUrl() {
      return this.action || config.baseUrl + this.$url.Upload.advert;
    },
    uploadHeaders() {
      return { token: this.token };
    },
    acceptTypes() {
      return this.accept;
    },
    // 显示的URL：图片直接显示，视频显示首帧
    displayUrl() {
      if (this.fileType === "video" && this.posterUrl) {
        return this.posterUrl;
      }
      if (this.fileType === "video") {
        return this.fileUrl ? "__video_placeholder__" : "";
      }
      return this.fileUrl;
    },
    displayMode() {
      if (this.fileType === "video" && !this.posterUrl) {
        return "video";
      }
      return "image";
    }
  },
  watch: {
    value: {
      immediate: true,
      handler(val) {
        if (val && val !== this.fileUrl) {
          this.fileUrl = val;
          this.fileType = this.valueType === 1 ? "video" : "image";
          if (this.fileType === "video") {
            this.$nextTick(() => {
              this.generateVideoPoster(val);
            });
          }
        } else if (!val) {
          this.reset();
        }
      }
    }
  },
  methods: {
    reset() {
      this.fileUrl = "";
      this.fileType = "";
      this.posterUrl = "";
      this.duration = 0;
      this.uploading = false;
      this.generatingPoster = false;
    },

    handleBeforeUpload(file) {
      const isImage = /\.(jpg|jpeg|png|gif)$/i.test(file.name);
      const isVideo = /\.(mp4|avi|mov|wmv|webm)$/i.test(file.name);
      
      if (!isImage && !isVideo) {
        this.$message.error(
          this.$t("mediaUpload.invalidType") || "Please upload valid image or video files"
        );
        return false;
      }
      
      // 检查文件大小
      const fileSizeMB = file.size / 1024 / 1024;
      if (isImage && fileSizeMB > this.imageMaxSize) {
        this.$message.error(
          `${this.$t("mediaUpload.imageTooLarge") || "Image size should not exceed"} ${this.imageMaxSize}MB`
        );
        return false;
      }
      if (isVideo && fileSizeMB > this.videoMaxSize) {
        this.$message.error(
          `${this.$t("mediaUpload.videoTooLarge") || "Video size should not exceed"} ${this.videoMaxSize}MB`
        );
        return false;
      }

      this.fileType = isImage ? "image" : "video";
      this.uploading = true;

      if (isVideo) {
        this.getVideoDuration(file);
      }

      return true;
    },

    getVideoDuration(file) {
      const url = URL.createObjectURL(file);
      const video = document.createElement("video");
      video.preload = "metadata";
      video.onloadedmetadata = () => {
        this.duration = Math.ceil(video.duration);
        URL.revokeObjectURL(url);
      };
      video.src = url;
    },
    
    handleSuccess(res, file) {
      this.uploading = false;
      // 清除el-upload内部文件列表，允许重新上传
      if (this.$refs.uploadRef) {
        this.$refs.uploadRef.clearFiles();
      }
      if (res.code === 0) {
        this.fileUrl = res.data;

        if (this.fileType === "video") {
          this.generatingPoster = true;
          this.$nextTick(() => {
            this.generateVideoPoster(res.data);
          });
        }

        this.$emit("success", {
          url: res.data,
          type: this.fileType === "video" ? 1 : 0,
          duration: this.duration
        });
        this.$emit("input", res.data);
        this.$emit("change", {
          url: res.data,
          type: this.fileType === "video" ? 1 : 0,
          duration: this.duration
        });
      } else {
        this.$message.warning(res.msg);
        this.reset();
      }
    },
    
    handleError(err) {
      this.uploading = false;
      // 清除el-upload内部文件列表
      if (this.$refs.uploadRef) {
        this.$refs.uploadRef.clearFiles();
      }
      let errorMessage = this.$t("mediaUpload.uploadFailed") || "Upload failed";
      if (err.response && err.response.data) {
        errorMessage = err.response.data.message || err.response.data.msg || errorMessage;
      } else if (err.message) {
        errorMessage = err.message;
      }
      this.$message.error(errorMessage);
      this.reset();
    },

    handleRemove() {
      this.reset();
      this.$emit("remove");
      this.$emit("input", "");
      this.$emit("change", { url: "", type: 0, duration: 0 });
    },

    generateVideoPoster(videoUrl) {
      if (!videoUrl) return;
      
      const video = document.createElement("video");
      video.crossOrigin = "anonymous";
      video.preload = "metadata";
      video.muted = true;
      
      video.onloadeddata = () => {
        const canvas = document.createElement("canvas");
        canvas.width = video.videoWidth || 320;
        canvas.height = video.videoHeight || 240;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        this.posterUrl = canvas.toDataURL("image/jpeg", 0.8);
        this.duration = Math.ceil(video.duration) || 0;
        this.generatingPoster = false;
      };
      
      video.onerror = () => {
        console.warn("Failed to load video for poster generation");
        this.generatingPoster = false;
      };
      
      video.src = videoUrl;
      video.currentTime = 0.1; // 跳到第一帧
    }
  }
};
</script>

<style lang="scss" scoped>
.el-upload-list--picture-card .el-upload-list__item {
  margin: 0;
}
.upload-wrapper {
  position: relative;
  display: inline-block;
}
.uploaded-image {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}
.uploaded-image img {
  width: 90%;
  height: 90%;
  object-fit: contain;
}
.uploaded-video-placeholder {
  width: 90%;
  height: 90%;
  border-radius: 8px;
  background: linear-gradient(135deg, #f5f7fa 0%, #e4e7ed 100%);
  color: #606266;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  text-align: center;
  padding: 8px;
  box-sizing: border-box;
}
.uploaded-video-placeholder i {
  font-size: 24px;
  color: #409eff;
}
.uploaded-video-placeholder span {
  font-size: 12px;
  line-height: 1.4;
}
.upload-loading {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
}
.upload-loading i {
  font-size: 24px;
  color: #409eff;
}
.upload-tips {
  margin-top: 8px;
}
.tip-item {
  color: #f56c6c;
  font-size: 12px;
  line-height: 1.4;
  white-space: nowrap;
}
</style>
